import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import {
  deriveMainRole,
  isDeveloperRole,
  permissionsForRoles,
  type MainRole,
  type Permission,
} from '@/auth/permissions';
import type { AuthUser, UserRole } from '@/types';

/**
 * Resolves the raw role-claim array for a user, with a legacy fallback.
 *
 * A session persisted BEFORE the RBAC platform has only the deprecated
 * `role` field and NO `roles[]`. We map those forward:
 *   'PlatformAdmin' → ['platformAdmin'], 'TenantAdmin' → ['admin'].
 * The `?? []` guards an old object that somehow lacks both.
 */
function resolveRoles(user: AuthUser | null): string[] {
  if (!user) return [];
  if (Array.isArray(user.roles) && user.roles.length > 0) return user.roles;
  // Legacy persisted session — derive from the bridge field.
  const legacy = user.role as UserRole | undefined;
  if (legacy === 'PlatformAdmin') return ['platformAdmin'];
  if (legacy === 'TenantAdmin') return ['admin'];
  return user.roles ?? [];
}

/**
 * Role/permission-based access control hook.
 *
 * Usage:
 *   const { hasPermission, isPlatformAdmin } = usePermission();
 *   if (!hasPermission('users.manage')) return <Forbidden />;
 */
export function usePermission() {
  const user = useAuthStore((s) => s.user);

  const roles = useMemo(() => resolveRoles(user), [user]);

  const permissions = useMemo<Set<Permission>>(
    () => permissionsForRoles(roles),
    [roles],
  );

  const mainRole = useMemo<MainRole>(() => deriveMainRole(roles), [roles]);
  const isDeveloper = useMemo(() => isDeveloperRole(roles), [roles]);

  // mainRole is the source of truth; for legacy sessions resolveRoles has
  // already mapped the bridge field into ['platformAdmin'] / ['admin'].
  const isPlatformAdmin = useMemo(() => mainRole === 'platformAdmin', [mainRole]);
  const isTenantAdmin = useMemo(() => mainRole === 'admin', [mainRole]);

  const hasPermission = (permission: Permission): boolean =>
    permissions.has(permission);

  const hasAnyPermission = (...perms: Permission[]): boolean =>
    perms.some((p) => permissions.has(p));

  // ── Back-compat: existing callers still use hasRole / hasAnyRole on the
  //    deprecated two-value UserRole.
  const hasRole = (role: UserRole): boolean => user?.role === role;

  const hasAnyRole = (...rolesArg: UserRole[]): boolean =>
    user != null && rolesArg.includes(user.role);

  return {
    user,
    permissions,
    mainRole,
    isDeveloper,
    isPlatformAdmin,
    isTenantAdmin,
    hasPermission,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
  };
}
